import React, { useEffect, useRef } from 'react';
import { Alert, Box, Slide, Theme } from '@mui/material';

import { closeSnackbar, dismissAction } from '@paragon/notification-tools';

import {
  getAllVisibleSnackbars,
  sortSnackbarsBySeverity,
  toMuiSeverity,
} from '../../store/selectors/notificationSelectors';
import { leftSidebarSet } from '../../store/slices/panelDataSlice';
import { useAppDispatch, useAppSelector } from '../hooks';

const SNACKBAR_TIMEOUT = 5_000;

export function DesignClientNotifier() {
  const dispatch = useAppDispatch();

  const timeoutHandleRef = useRef<number | null>(null);

  const snackbars = useAppSelector(getAllVisibleSnackbars);
  const notificationsPanelOpen = useAppSelector((state) => state.panelData.leftSidebar === 'notifications');

  useEffect(() => {
    if (timeoutHandleRef.current != null) {
      clearTimeout(timeoutHandleRef.current);
    }

    const expiringSnackbars = snackbars.filter(([, options]) => !options.persist);
    if (expiringSnackbars.length === 0) return;

    const expirationTimestamps = expiringSnackbars.map(([, options]) => options.addedAt + SNACKBAR_TIMEOUT);

    function clearExpiredSnackbars() {
      for (const [, { key, addedAt }] of expiringSnackbars) {
        if (addedAt + SNACKBAR_TIMEOUT < Date.now()) {
          dispatch(closeSnackbar(key));
        }
      }
    }

    if (expirationTimestamps.some((timestamp) => timestamp < Date.now())) {
      clearExpiredSnackbars();
    } else {
      const timeToNextExpiration = Math.min(...expirationTimestamps) - Date.now();

      timeoutHandleRef.current = setTimeout(clearExpiredSnackbars, timeToNextExpiration);
    }
  }, [dispatch, snackbars]);

  let content = null;

  if (snackbars.length > 0) {
    const worstSnackbar = sortSnackbarsBySeverity(snackbars)[0];
    const interactiveSnackbar = snackbars.find(([, options]) => options.interactive);
    const worstPermanentSnackbar = sortSnackbarsBySeverity(snackbars.filter(([, options]) => options.permanent))[0];

    // choosing which notification to show: a permanent notification must never be masked, then a snackbar for an
    //   ongoing user interaction takes priority, otherwise go with the oldest one with the worst severity
    const [message, options] = worstPermanentSnackbar ?? interactiveSnackbar ?? worstSnackbar;

    const color = toMuiSeverity(options.variant);

    content = (
      <Alert
        key={options.key}
        variant="filled"
        severity={color}
        sx={{
          color: 'white',
          backgroundColor: (theme) => theme.palette[color].dark,
        }}
        onClick={() => {
          dispatch(leftSidebarSet(notificationsPanelOpen ? 'open' : 'notifications'));
        }}
        onClose={
          // set `onClose` (which causes MUI to show a native dismiss button) when needed to override the dismiss action
          //   added by the notification tools library
          options.action === dismissAction
            ? (event) => {
                event.stopPropagation();
                dispatch(closeSnackbar(options.key));
              }
            : undefined
        }
        action={
          // don't provide action button if it's the default dismiss action, since it's overridden above
          options.action && options.action !== dismissAction
            ? typeof options.action === 'function'
              ? options.action(options.key)
              : options.action
            : undefined
        }
      >
        {message}
      </Alert>
    );
  }

  return (
    <Slide direction="up" in={snackbars.length > 0 && !notificationsPanelOpen}>
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,

          padding: 2,
          cursor: 'pointer',

          zIndex: (theme: Theme) => theme.zIndex.drawer - 1,
        }}
      >
        {content}
      </Box>
    </Slide>
  );
}
